import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen, Books } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { Seo } from "@/components/Seo";
import { FooterLinks } from "@/components/FooterLinks";
import { RESOURCE_INDEX, GUIDES, SOFTWARE_APP_SCHEMA } from "@/pages/resources/resourcesData";

const SITE = "https://getlcshield.com";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Resources() {
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "LC Shield Trade Finance Resource Center",
    url: `${SITE}/resources`,
    description: RESOURCE_INDEX.intro,
    hasPart: GUIDES.map((g) => ({
      "@type": "Article",
      headline: g.title,
      description: g.description,
      url: `${SITE}/resources/${g.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-lc-navy text-white font-sans antialiased flex flex-col" data-testid="resources-index">
      <Seo
        path="/resources"
        title="Trade Finance Resource Center | LC Shield — UCP 600, MT700 & ISBP 821 (2023) Guides"
        description="Free expert guides on UCP 600 discrepancies, SWIFT MT700 field audits and ISBP 821 (2023) bill of lading rules — for exporters and documentary-credit teams who need to present clean."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_APP_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      {/* NAV */}
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-lc-navy/85 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="resources-logo">
            <LCShieldLogo className="text-lc-sky" size={24} />
            <span className="font-serif text-xl tracking-tight">LC Shield</span>
          </Link>
          <button onClick={() => nav("/workspace")} data-testid="resources-open-workspace"
            className="inline-flex items-center gap-2 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
            Open Workspace <ArrowRight weight="bold" size={15} />
          </button>
        </div>
      </nav>

      {/* HEADER */}
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-lc-fog hover:text-white transition-colors duration-200">
            <ArrowLeft size={13} weight="bold" /> Back to home
          </Link>
          <motion.div initial="hidden" animate="show">
            <motion.div variants={fade} custom={0} className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-lc-sky">
              <Books size={16} weight="duotone" /> {RESOURCE_INDEX.overline}
            </motion.div>
            <motion.h1 variants={fade} custom={1} className="mt-4 font-serif font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {RESOURCE_INDEX.title}
            </motion.h1>
            <motion.p variants={fade} custom={2} className="mt-6 max-w-2xl text-lg text-lc-fog leading-relaxed">
              {RESOURCE_INDEX.intro}
            </motion.p>
          </motion.div>
        </div>
      </header>

      {/* GUIDE CARDS */}
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 py-16">
          <motion.div initial="hidden" animate="show" className="grid gap-6 md:grid-cols-3">
            {GUIDES.map((g, i) => (
              <motion.div key={g.slug} variants={fade} custom={i}>
                <Link to={`/resources/${g.slug}`} data-testid={`resource-card-${g.slug}`}
                  className="group flex h-full flex-col rounded-sm border border-white/10 bg-lc-slate/30 p-7 transition-colors duration-200 hover:border-lc-sky/50 hover:bg-lc-slate/50">
                  <div className="flex items-center justify-between">
                    <span className="rounded-sm border border-lc-sky/30 bg-lc-sky/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-lc-sky">
                      {g.category}
                    </span>
                    <BookOpen size={22} weight="duotone" className="text-lc-fog group-hover:text-lc-sky transition-colors duration-200" />
                  </div>
                  <h2 className="mt-5 font-serif text-2xl leading-snug text-white group-hover:text-lc-sky transition-colors duration-200">
                    {g.title}
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-lc-fog">{g.description}</p>
                  <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
                    <span className="font-mono text-[11px] text-lc-fog">{g.readTime}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-lc-sky">
                      Read guide <ArrowRight weight="bold" size={14} className="group-hover:translate-x-0.5 transition-transform duration-200" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LCShieldLogo className="text-lc-sky" size={20} />
            <span className="font-serif text-lg text-white">LC Shield</span>
          </div>
          <FooterLinks variant="dark" />
        </div>
      </footer>
    </div>
  );
}
